import { useRef, useState } from "react";

import { randomUUID } from "../lib/utils";
import { openCloudConversation } from "./cloudConversations";
import {
  deskBirthGoesToWorld,
  deskBirthMacTools,
  isBirthMacRefusal,
  deskBirthRouteMatters,
  ROUTE_UNANSWERED_REASON,
  useDeskConversationRoute,
  type DeskMacTools,
} from "./deskConversationRoute";
import { useRootSessionDefaults } from "./rootSessionDefaults";
import { announceRootSessionModelNote } from "./rootSessionModelSources";
import { readRoomEntryAsPerson, refusalOfWorldError } from "./webHostWorldDoors";
import { readWorldConvexClient } from "./worldConvexClient";
import { openWorldRoomEntry } from "./worldSessionClient";
import {
  recordWorldSessionBirthAttempt,
  type WorldSessionBirthAttemptRecord,
} from "./worldSessionBirthAttemptsClient";
import { useNeucleosWorldStore } from "./worldStore";

export const NEW_ROOM_LABEL = "New room";

/**
 * Shared by the dock + and the collection tray: the same birth, identity and retry.
 *
 * One-backend plan step 4 (20 Oct 2026): where the World's switch says so for this World and this Desk names no folder
 * for it (deskConversationRoute.ts, the rule the Workroom + has used since step 3), the new room and its conversation
 * are born in the World itself (cleoConversations:openOrCreate with a general room the World makes, the conversation
 * as the room's root session), opened and read back with the window's own sign-in: the session is `S_cloud_…`, kept in
 * the World, the same record Pocket and the browser show, and Cleo answers it through the World. Everywhere else the
 * Mac's room door, exactly as before.
 */
export function useNewGeneralRoom(worldKey: string) {
  const defaults = useRootSessionDefaults(worldKey);
  const { routeAt, routeSettledAt, folderSettingAt, macToolsAt, macToolsSettledAt } =
    useDeskConversationRoute(worldKey || null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attempt = useRef<{
    worldKey: string;
    roomId: string;
    sessionId: string;
    execution?: WorldSessionBirthAttemptRecord | null;
    /** F37: why this room does not start on Neucleos Fast, said once it opens. */
    note?: string;
    /**
     * The World birth's own key (the conversation openOrCreate names): kept while the World may have opened the
     * conversation (its answer lost, or its read-back refused), so the next press reaches that same conversation
     * instead of making a second; let go on success or when the World said it wrote nothing. One person's.
     */
    world?: { readonly clientKey: string; readonly identity: string | null };
  } | null>(null);
  const inFlight = useRef(false);

  async function createInWorld(
    client: NonNullable<ReturnType<typeof readWorldConvexClient>>,
    coordinates: NonNullable<typeof attempt.current>,
    birthMac?: { readonly mac: string },
  ) {
    const identity = useNeucleosWorldStore.getState().identity;
    if (coordinates.world === undefined || coordinates.world.identity !== identity)
      coordinates.world = { clientKey: randomUUID(), identity };
    const { clientKey } = coordinates.world;
    let born;
    try {
      born = await openCloudConversation(
        client,
        worldKey,
        clientKey,
        undefined,
        {
          roomId: coordinates.roomId,
          label: NEW_ROOM_LABEL,
          kind: "general",
        },
        undefined,
        birthMac,
      );
    } catch (cause) {
      const refusal = refusalOfWorldError(cause);
      // The World's refusals end "0 writes": nothing was born, so the next press is a fresh open. A folder birth refused because
      // it cannot be this Mac's starts afresh too, with new coordinates (whatever door the next press takes).
      if (isBirthMacRefusal(refusal.body.reason)) attempt.current = null;
      else if (refusal.body.reason.includes("0 writes")) delete coordinates.world;
      setError(refusal.body.reason);
      return null;
    }
    const read = await readRoomEntryAsPerson(client, {
      worldKey,
      roomId: born.roomId,
      sessionId: born.sessionId,
    });
    if (!read.ok) {
      // The World holds the conversation; the key stays, so Retry reaches it.
      setError(`${read.reason} Click + again to reach the conversation the World opened.`);
      return null;
    }
    attempt.current = null;
    return {
      worldKey,
      roomId: born.roomId,
      sessionId: born.sessionId,
      entry: read.entry,
      keptInWorld: true as const,
    };
  }

  async function create() {
    if (inFlight.current) return null;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    const coordinates =
      attempt.current?.worldKey === worldKey
        ? attempt.current
        : {
            worldKey,
            roomId: `R_${randomUUID().replaceAll("-", "")}`,
            sessionId: `S_${randomUUID().replaceAll("-", "")}`,
          };
    attempt.current = coordinates;
    try {
      // The World route, decided at the press from the World's last answer and this Desk's folder setting; a new general
      // room is always one the World can make, so the Workroom rule's "known" is given.
      const pressedClient = readWorldConvexClient();
      // W-3f25c9ff: a press before the World's first route answer waits for it (bounded) instead of deciding "mac", unless the
      // Desk names another folder for the World (the Mac's then, whatever the World answers). The folder setting is read again
      // after the wait: one that arrived meanwhile counts (Codex review r1 of #3183).
      const settled = deskBirthRouteMatters({
        intent: "new",
        folderSetting: folderSettingAt(worldKey),
        workroomKnown: true,
      })
        ? await routeSettledAt(worldKey)
        : { route: routeAt(pressedClient, worldKey), client: pressedClient };
      if (settled === "cancelled") return null;
      if (settled === "unanswered") {
        setError(ROUTE_UNANSWERED_REASON);
        return null;
      }
      // The World's answer and the client it came through: a World birth uses exactly that client, never one read afterwards
      // (a client gone by then must not turn a World answer into a Mac birth; Codex review r3 of #3183).
      const { route, client } = settled;
      if (route === "world" && client === null) {
        setError(ROUTE_UNANSWERED_REASON);
        return null;
      }
      const folderSetting = folderSettingAt(worldKey);
      // A folder birth waits, once and briefly, for this Mac's switch when it has not been read yet; any other press decides now.
      const macTools: DeskMacTools =
        folderSetting === "explicit-here" && route === "world"
          ? await macToolsSettledAt(client, worldKey)
          : macToolsAt(client, worldKey);
      if (
        client !== null &&
        deskBirthGoesToWorld({
          intent: "new",
          route,
          folderSetting,
          workroomKnown: true,
          macTools,
        })
      )
        // A folder conversation records this Mac, so its folder tools use this Mac's folders (steps 5-7 design §2.3).
        return await createInWorld(
          client,
          coordinates,
          deskBirthMacTools({ folderSetting, macTools }),
        );
      if (coordinates.execution === undefined) {
        const prepared = await defaults.prepareExecution({
          sessionId: coordinates.sessionId,
          label: NEW_ROOM_LABEL,
        });
        if (!prepared.ok) {
          setError(prepared.reason);
          return null;
        }
        coordinates.execution = prepared.execution;
        if (prepared.note !== undefined) coordinates.note = prepared.note;
      }
      if (coordinates.execution) {
        const recorded = await recordWorldSessionBirthAttempt(coordinates.execution);
        if (!recorded.ok) {
          setError("The session could not be reserved. Retry keeps the same room.");
          return null;
        }
      }
      const outcome = await openWorldRoomEntry({
        worldKey,
        kind: "general",
        roomId: coordinates.roomId,
        sessionId: coordinates.sessionId,
        label: NEW_ROOM_LABEL,
        goalIds: [],
      });
      if (!outcome.ok) {
        setError(outcome.reason);
        return null;
      }
      attempt.current = null;
      if (coordinates.note !== undefined) announceRootSessionModelNote(coordinates.note);
      return { ...coordinates, entry: outcome.entry };
    } catch {
      setError("The room could not be opened. Retry checks the same room.");
      return null;
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }
  return { create, submitting, error };
}
